import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import EventCard from "@/components/EventCard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, Sparkles, Calendar, Users, Crown, Camera, Music, ArrowRight, Zap, TrendingUp } from "lucide-react";

const cityToSlug = (c) => (c || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const pretty = (slug) => decodeURIComponent(slug || "").split("-").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const isPast = (e) => {
  try {
    const d = e.end_date || e.date;
    if (!d) return false;
    const dt = new Date(d.includes("T") ? d : `${d}T23:59:59`);
    return dt < new Date();
  } catch { return false; }
};

const isThisWeekend = (e) => {
  try {
    const d = new Date((e.date || "").includes("T") ? e.date : `${e.date}T00:00:00`);
    const now = new Date();
    const day = now.getDay(); // 0 sun … 6 sat
    const fri = new Date(now); fri.setDate(now.getDate() + ((5 - day + 7) % 7)); fri.setHours(0, 0, 0, 0);
    const sun = new Date(fri); sun.setDate(fri.getDate() + 2); sun.setHours(23, 59, 59, 0);
    return d >= fri && d <= sun;
  } catch { return false; }
};

// Curated city vibe copy — falls back to a sensible default for unknown cities.
const CITY_VIBES = {
  kolkata: { tag: "The city of poets & parties", accent: "#FF2E9A", blurb: "From Park Street rooftops to Salt Lake fests — Kolkata's live culture, in one app." },
  mumbai: { tag: "Never sleeps. Never repeats.", accent: "#FF6B00", blurb: "Bandra house parties, Powai concerts, Colaba comedy — all the vibes, all in one place." },
  delhi: { tag: "Where the crowd shows up", accent: "#7B2FFF", blurb: "Hauz Khas gigs, GK after-parties, DLF Cyber Hub launches — Delhi lives on Eventom." },
  bangalore: { tag: "Where the scene runs late", accent: "#2E6BFF", blurb: "Indiranagar breweries, HSR pop-ups, Koramangala indie nights — every crowd, one app." },
  patna: { tag: "The scene is here.", accent: "#D4FF00", blurb: "College fests, private parties, launches — Patna's biggest nights land on Eventom first." },
  bhubaneswar: { tag: "Odisha's live capital", accent: "#00E6C3", blurb: "Fests, concerts and creator meetups — Bhubaneswar's live culture, curated." },
  ranchi: { tag: "The mountain scene rises", accent: "#FF2E9A", blurb: "Ranchi's rising fest and college circuit — book, vibe, and find your photos." },
  guwahati: { tag: "The North-East beat", accent: "#7B2FFF", blurb: "Music, festivals and college nights — Guwahati's crew on Eventom." },
};

const defaultVibe = { tag: "Live culture. On tap.", accent: "#D4FF00", blurb: "Every kind of event happening in your city — one app, QR entry, AI photos." };


export default function CityEventsPage() {
  const { slug } = useParams();
  const cityName = pretty(slug);
  const vibe = CITY_VIBES[(slug || "").toLowerCase()] || defaultVibe;
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [partners, setPartners] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `${cityName} events, parties, VIP — Eventom`;
    const citySlug = cityToSlug(decodeURIComponent(slug || ""));
    setLoading(true);
    Promise.all([
      api.get("/events?limit=200").then(({ data }) => (data || []).filter((e) => cityToSlug(e.city) === citySlug)).catch(() => []),
      api.get("/ecosystem/partners").then(({ data }) => (data || []).filter((p) => cityToSlug(p.city) === citySlug).slice(0, 8)).catch(() => []),
      api.get(`/blogs?tag=${citySlug}`).then(({ data }) => (data || []).slice(0, 3)).catch(() => []),
    ]).then(([ev, prt, bl]) => { setEvents(ev); setPartners(prt); setBlogs(bl); }).finally(() => setLoading(false));
  }, [slug, cityName]);

  const upcoming = useMemo(() => events.filter((e) => !isPast(e)).sort((a, b) => new Date(a.date) - new Date(b.date)), [events]);
  const weekendPicks = useMemo(() => upcoming.filter(isThisWeekend).slice(0, 6), [upcoming]);
  const past = useMemo(() => events.filter(isPast).slice(0, 8), [events]);

  const venues = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      const v = e.location_name || e.venue;
      if (v) map.set(v, (map.get(v) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, count]) => ({ name, count }));
  }, [events]);

  const shareIntent = `https://wa.me/?text=${encodeURIComponent(`${cityName} on Eventom — every event, one app 👉 https://eventom.online/city/${slug}`)}`;

  return (
    <Layout>
      {/* HERO — city-native colour + vibe copy */}
      <section className="relative overflow-hidden border-b border-line" data-testid="city-hero"
        style={{ background: `radial-gradient(ellipse at top, ${vibe.accent}33, transparent 55%), radial-gradient(ellipse at bottom right, ${vibe.accent}22, transparent 60%), #0A0A0A` }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="chip mb-3" style={{ color: vibe.accent, borderColor: `${vibe.accent}66` }}><MapPin className="w-3 h-3" /> {cityName}</div>
          <h1 className="font-fancy text-4xl sm:text-6xl md:text-7xl leading-[0.9]">What are you up for<br />in <span style={{ color: vibe.accent }}>{cityName}?</span></h1>
          <p className="mt-4 text-text/85 max-w-xl">{vibe.blurb}</p>

          {/* Local stats */}
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3" data-testid="city-stats">
            <div><div className="font-fancy text-3xl leading-none" style={{ color: vibe.accent }}>{upcoming.length}</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">upcoming events</div></div>
            <div><div className="font-fancy text-3xl leading-none text-white">{partners.length}</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">local partners</div></div>
            <div><div className="font-fancy text-3xl leading-none text-white">{venues.length}</div><div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">venues live</div></div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#weekend" className="btn-primary" data-testid="city-cta-weekend"><Zap className="w-4 h-4" /> This weekend</a>
            <Link to={`/vip?city=${slug}`} className="btn-ghost" data-testid="city-cta-vip"><Crown className="w-4 h-4" /> {cityName} VIP</Link>
            <a href={shareIntent} target="_blank" rel="noreferrer" data-testid="city-cta-share" className="btn-lime">Share {cityName} scene →</a>
          </div>
        </div>
      </section>

      {/* THIS WEEKEND — Hooked-style curated shelf */}
      <section id="weekend" className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="city-weekend">
        <div className="flex items-end justify-between gap-3 mb-6">
          <div>
            <div className="chip !text-lime !border-lime/40 mb-3"><Calendar className="w-3 h-3" /> This weekend in {cityName}</div>
            <h2 className="font-display uppercase text-3xl md:text-4xl">The picks. <span className="grad-text-hot">Hand-curated.</span></h2>
          </div>
          <Link to="/#events-grid" className="text-[11px] font-mono uppercase tracking-widest text-lime">All events →</Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">{[0, 1, 2, 3].map((i) => <div key={i} className="glass rounded-3xl aspect-[4/5] animate-pulse" />)}</div>
        ) : weekendPicks.length === 0 ? (
          <div className="glass rounded-3xl p-10 text-center" data-testid="weekend-empty">
            <Sparkles className="w-8 h-8 text-lime mx-auto mb-3" />
            <div className="font-display text-2xl">Weekend's quiet — for now.</div>
            <div className="text-sm text-muted mt-1">New drops land Wednesday. Meanwhile, check every event live in {cityName} below.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-5" data-testid="weekend-grid">
            {weekendPicks.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        )}
      </section>

      {/* LOCAL PARTNERS — city-native ecosystem */}
      {partners.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 border-t border-line" data-testid="city-partners">
          <div className="flex items-end justify-between gap-3 mb-6">
            <div>
              <div className="chip !text-magenta !border-magenta/40 mb-3"><Users className="w-3 h-3" /> Local ecosystem</div>
              <h2 className="font-display uppercase text-3xl md:text-4xl">{cityName} <span className="grad-text-hot">crew.</span></h2>
              <p className="text-sm text-muted mt-2 max-w-md">Artists, vendors, creators and venues who ship the {cityName} scene.</p>
            </div>
            <Link to={`/ecosystem?city=${slug}`} className="text-[11px] font-mono uppercase tracking-widest text-magenta">All partners →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="city-partner-grid">
            {partners.map((p) => (
              <Link key={p.id} to={`/ecosystem?genre=${p.type}`} data-testid={`city-partner-${p.id}`} className="glass rounded-3xl overflow-hidden card-lift">
                {p.image_url || p.profile_photo ? (
                  <div className="aspect-video overflow-hidden"><img src={p.image_url || p.profile_photo} alt={p.name} className="w-full h-full object-cover" /></div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-magenta/25 via-purple/25 to-lime/10 flex items-center justify-center"><Music className="w-8 h-8 text-white/40" /></div>
                )}
                <div className="p-4">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-lime">{p.type}</div>
                  <div className="font-heading font-bold text-sm mt-1 truncate">{p.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* TOP VENUES — social proof */}
      {venues.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 border-t border-line" data-testid="city-venues">
          <div className="chip !text-skyx !border-skyx/40 mb-3"><TrendingUp className="w-3 h-3" /> Trending venues</div>
          <h2 className="font-display uppercase text-3xl md:text-4xl">Where <span className="grad-text-hot">{cityName}</span> shows up.</h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {venues.map((v) => (
              <div key={v.name} className="glass rounded-2xl p-4 text-center" data-testid={`venue-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-fancy text-2xl text-skyx leading-none">{v.count}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">events</div>
                <div className="font-heading font-bold text-xs mt-2 line-clamp-2">{v.name}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CITY BLOGS — SEO + editorial */}
      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 border-t border-line" data-testid="city-blogs">
          <div className="chip !text-purple !border-purple/40 mb-3">City journal</div>
          <h2 className="font-display uppercase text-3xl md:text-4xl">Stories from <span className="grad-text-hot">{cityName}</span></h2>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {blogs.map((b) => (
              <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`city-blog-${b.slug}`} className="glass rounded-3xl overflow-hidden card-lift">
                {b.cover_url ? <div className="aspect-video overflow-hidden"><img src={b.cover_url} alt={b.title} className="w-full h-full object-cover" /></div> : <div className="aspect-video bg-purple/15" />}
                <div className="p-4">
                  <div className="font-display text-lg uppercase leading-tight line-clamp-2">{b.title}</div>
                  {b.excerpt && <div className="text-xs text-muted mt-1.5 line-clamp-2">{b.excerpt}</div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ALL EVENTS (upcoming + past) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 border-t border-line">
        <h2 className="font-display uppercase text-3xl md:text-4xl mb-6">All upcoming in {cityName}</h2>
        {upcoming.length === 0 ? (
          <div className="glass rounded-3xl p-12 text-center" data-testid="city-empty">
            <Sparkles className="w-10 h-10 text-lime mx-auto mb-3" />
            <div className="font-display text-2xl">Nothing live in {cityName} right now.</div>
            <p className="text-white/60 mt-2 text-sm">New drops land every week — check all events or bring your own to {cityName}.</p>
            <div className="mt-5 flex justify-center gap-3 flex-wrap">
              <Link to="/" className="btn-lime">Browse All Events</Link>
              <Link to="/organisers" className="btn-ghost">List an Event Here</Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="city-events-grid">
            {upcoming.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        )}

        {past.length > 0 && (
          <>
            <h3 className="mt-14 font-display text-xl text-white/60">Recently ended in {cityName}</h3>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {past.map((e, i) => <EventCard key={e.id} event={e} index={i} isPast />)}
            </div>
          </>
        )}

        {/* Local CTA strip */}
        <div className="mt-14 glass rounded-3xl p-8 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${vibe.accent}22, transparent)` }}>
          <div className="grid md:grid-cols-2 items-center gap-4">
            <div>
              <div className="chip mb-3" style={{ color: vibe.accent, borderColor: `${vibe.accent}66` }}>Host in {cityName}?</div>
              <h3 className="font-display uppercase text-3xl leading-tight">Ship your next {cityName} night on Eventom.</h3>
              <p className="text-sm text-muted mt-2">Tickets, QR entry, AI Gallery, contests — one dashboard. Zero setup.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <Link to="/organisers" className="btn-primary" data-testid="city-organiser-cta">List an Event <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/work-with-eventom" className="btn-ghost" data-testid="city-work-cta">Work with us</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
